import { useEffect } from 'react'

/**
 * Hook that automatically adds data-dd-privacy="hidden" and class="dd-privacy-hidden"
 * to all img and svg elements in the DOM at runtime (web only)
 * setting the privacy level to mask, doesn't work somehow as the images are still visible.
 */
export const usePrivacyMask = (): void => {
  useEffect(() => {
    const addPrivacyAttributes = (element: Element): void => {
      // Check if the element already has the required attributes
      const hasDataAttribute = element.hasAttribute('data-dd-privacy')
      const hasClassAttribute = element.classList.contains('dd-privacy-hidden')

      if (hasDataAttribute && hasClassAttribute) {
        return // Already has both attributes, skip
      }

      // Add data-dd-privacy="hidden" if not present
      if (!hasDataAttribute) {
        element.setAttribute('data-dd-privacy', 'hidden')
      }

      // Add dd-privacy-hidden class if not present
      if (!hasClassAttribute) {
        element.classList.add('dd-privacy-hidden')
      }
    }

    // Process existing img and svg elements
    const existingImages = document.querySelectorAll('img')
    const existingSvgs = document.querySelectorAll('svg')
    existingImages.forEach(addPrivacyAttributes)
    existingSvgs.forEach(addPrivacyAttributes)

    // Set up MutationObserver to watch for new img and svg elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Check for added nodes
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const elementNode = node as Element

            // If the added node is an img or svg element
            if (elementNode.tagName === 'IMG' || elementNode.tagName === 'SVG') {
              addPrivacyAttributes(elementNode)
            }

            // Check for img and svg elements within the added node
            const imgElements = elementNode.querySelectorAll?.('img')
            const svgElements = elementNode.querySelectorAll?.('svg')
            if (imgElements) {
              imgElements.forEach(addPrivacyAttributes)
            }
            if (svgElements) {
              svgElements.forEach(addPrivacyAttributes)
            }
          }
        })
      })
    })

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    // Cleanup
    return () => {
      observer.disconnect()
    }
  }, [])
}
